'use client'

import { useTranslation } from '@shared/config/i18n'
import { UI_IDS } from '@shared/constants'
import { clsx } from 'clsx'
import { MessageCircle } from 'lucide-react'
import Image from 'next/image'

import { ButtonV1, Typography } from '@podo-app/design-system-temp'

type ButtonProps = Parameters<typeof ButtonV1>[0]

type Props = {
  appearance?: 'default' | 'jp'
  className?: string
  href?: string
  label?: string
  onClick?: () => boolean | void
} & Omit<ButtonProps, 'onClick'>

const LineLoginButton = ({ appearance = 'default', className, disabled, href, label, onClick, ...props }: Props) => {
  const t = useTranslation()

  const content = (
    <>
      {appearance === 'jp' ? (
        <span className="relative size-6 shrink-0 overflow-hidden" aria-hidden>
          <Image src="/images/login/line-login.svg" alt="" fill sizes="24px" />
        </span>
      ) : (
        <MessageCircle aria-hidden size={24} strokeWidth={2.5} />
      )}
      <Typography
        size={appearance === 'jp' ? 'body1' : 'h3'}
        className={clsx('text-white', appearance === 'jp' && 'font-bold !leading-6')}
      >
        {label ?? t('common.auth.lineLogin')}
      </Typography>
    </>
  )

  const buttonClassName = clsx(
    'line_login_btn',
    'flex w-full items-center justify-center bg-[#06C755] py-2.5 text-white hover:bg-[#20D769]',
    appearance === 'jp' ? 'gap-2 rounded-xl' : 'gap-[5px] rounded-[7.5px]',
    disabled && 'cursor-not-allowed bg-gray-200 hover:bg-gray-200',
    className,
  )

  if (href) {
    return (
      <ButtonV1
        asChild
        size="xLarge"
        className={buttonClassName}
        eventParams={{
          name: UI_IDS.btn.lineLogin,
          location: 'login',
          action: 'submit',
        }}
        {...props}
      >
        <a
          href={href}
          aria-disabled={disabled || undefined}
          onClick={(event) => {
            if (disabled || onClick?.() === false) {
              event.preventDefault()
            }
          }}
        >
          {content}
        </a>
      </ButtonV1>
    )
  }

  return (
    <ButtonV1
      size="xLarge"
      className={buttonClassName}
      disabled={disabled}
      eventParams={{
        name: UI_IDS.btn.lineLogin,
        location: 'login',
        action: 'submit',
      }}
      onClick={() => onClick?.()}
      {...props}
    >
      {content}
    </ButtonV1>
  )
}

export default LineLoginButton
